'use client'
import { cva } from 'class-variance-authority'
import { buttonVariants } from 'fumadocs-ui/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from 'fumadocs-ui/components/ui/popover'
import { useCopyButton } from 'fumadocs-ui/utils/use-copy-button'
import { Check, ChevronDown, Copy, ExternalLinkIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { cn } from '../../lib/cn'

const cache = new Map<string, string>()

export function LLMCopyButton({
  /**
   * A URL to fetch the raw Markdown/MDX content of page
   */
  markdownUrl,
}: {
  markdownUrl: string
}) {
  const [isLoading, setLoading] = useState(false)
  const [checked, onClick] = useCopyButton(async () => {
    const cached = cache.get(markdownUrl)
    if (cached) return navigator.clipboard.writeText(cached)

    setLoading(true)

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': fetch(markdownUrl).then(async (res) => {
            const content = await res.text()
            cache.set(markdownUrl, content)

            return content
          }),
        }),
      ])
    } finally {
      setLoading(false)
    }
  })

  return (
    <button
      disabled={isLoading}
      className={cn(
        buttonVariants({
          color: 'secondary',
          size: 'sm',
          className: 'gap-2 [&_svg]:size-3.5 [&_svg]:text-fd-muted-foreground',
        }),
      )}
      onClick={onClick}>
      {checked ?
        <Check />
      : <Copy />}
      Copy Markdown
    </button>
  )
}

const optionVariants = cva(
  'text-sm p-2 rounded-lg inline-flex items-center gap-2 hover:text-fd-accent-foreground hover:bg-fd-accent [&_svg]:size-4',
)

/** The hosted MCP server, as the MCP Quickstart sets it up in every client: OAuth on first use, nothing to install. */
const MCP_URL = 'https://mcp.auto.dev/mcp'
const MCP_NAME = 'auto-dev'

type Mark = { src: string; mono: boolean }
type Item = { title: string; href: string; mark: Mark; newTab: boolean }

const MARKDOWN: Mark = { src: '/logos/markdown.svg', mono: true }
const OPENAI: Mark = { src: '/logos/ai/openai.svg', mono: true }
const CLAUDE: Mark = { src: '/logos/ai/claude.svg', mono: false }
const CURSOR: Mark = { src: '/logos/ai/cursor.svg', mono: true }
const VSCODE: Mark = { src: '/logos/ai/vscode.svg', mono: false }

/** Brand marks as the site shows them: one-color marks take the text color, the others keep theirs. */
function BrandMark({ mark }: { mark: Mark }) {
  if (!mark.mono) return <img src={mark.src} alt='' className='size-4' />
  return (
    <span
      aria-hidden='true'
      className='size-4 shrink-0 bg-current'
      style={{
        maskImage: `url(${mark.src})`,
        WebkitMaskImage: `url(${mark.src})`,
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
      }}
    />
  )
}

function CopyMcpUrl() {
  const [checked, onClick] = useCopyButton(() => navigator.clipboard.writeText(MCP_URL))
  return (
    <button type='button' onClick={onClick} className={cn(optionVariants(), '[&_svg]:text-fd-muted-foreground')}>
      {checked ?
        <Check />
      : <Copy />}
      Copy MCP server URL
    </button>
  )
}

export function ViewOptions({
  markdownUrl,
}: {
  /**
   * A URL to the raw Markdown/MDX content of page
   */
  markdownUrl: string
}) {
  const [pageItems, mcpItems] = useMemo(() => {
    const fullMarkdownUrl = typeof window !== 'undefined' ? new URL(markdownUrl, window.location.origin) : 'loading'
    const q = `Read ${fullMarkdownUrl}, help me integrate this Auto.dev automotive API endpoint into my application`

    // "I'm building an automotive app and need help implementing this API endpoint"
    // "Explain how to use this automotive data API in my development project"
    // "Help me understand and integrate this Auto.dev API endpoint"

    const page: Item[] = [
      { title: 'View as Markdown', href: markdownUrl, mark: MARKDOWN, newTab: true },
      { title: 'Open in ChatGPT', href: `https://chatgpt.com/?${new URLSearchParams({ hints: 'search', q })}`, mark: OPENAI, newTab: true },
      { title: 'Open in Claude', href: `https://claude.ai/new?${new URLSearchParams({ q })}`, mark: CLAUDE, newTab: true },
      { title: 'Open in Cursor', href: `https://cursor.com/link/prompt?${new URLSearchParams({ text: q })}`, mark: CURSOR, newTab: true },
    ]
    // One-click installs of the hosted server, with the config the MCP Quickstart gives each editor
    const mcp: Item[] = [
      {
        title: 'Install MCP in Cursor',
        href: `cursor://anysphere.cursor-deeplink/mcp/install?${new URLSearchParams({ name: MCP_NAME, config: btoa(JSON.stringify({ url: MCP_URL })) })}`,
        mark: CURSOR,
        newTab: false,
      },
      {
        title: 'Install MCP in VS Code',
        href: `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: MCP_NAME, type: 'http', url: MCP_URL }))}`,
        mark: VSCODE,
        newTab: false,
      },
    ]
    return [page, mcp]
  }, [markdownUrl])

  const link = (item: Item) => (
    <a
      key={item.title}
      href={item.href}
      {...(item.newTab ? { rel: 'noreferrer noopener', target: '_blank' } : {})}
      className={cn(optionVariants())}>
      <BrandMark mark={item.mark} />
      {item.title}
      {item.newTab && <ExternalLinkIcon className='text-fd-muted-foreground size-3.5 ms-auto' />}
    </a>
  )

  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          buttonVariants({
            color: 'secondary',
            size: 'sm',
            className: 'gap-2',
          }),
        )}>
        Open
        <ChevronDown className='size-3.5 text-fd-muted-foreground' />
      </PopoverTrigger>
      <PopoverContent className='flex flex-col overflow-auto'>
        {pageItems.map(link)}
        <div role='separator' className='my-1 h-px bg-fd-border' />
        {mcpItems.map(link)}
        <CopyMcpUrl />
      </PopoverContent>
    </Popover>
  )
}
